/** Portrait/landscape layout orchestrator — repositions all UI elements on resize */

import { Application, Container, Graphics } from 'pixi.js';
import { GameConfig } from '../config/GameConfig';
import { ReelSet } from '../reels/ReelSet';
import { Logger } from '../utils/Logger';

const logger = new Logger('ResponsiveLayout');

export type Orientation = 'landscape' | 'portrait';

export interface LayoutElements {
  gameRoot: Container;
  reelSet: Container;
  reelBg: Container;
  reelFrame?: Container;  // Chrome reel frame (shown in landscape, hidden in portrait)
  reelFrameFg?: Container;  // Foreground overlay (glowing borders, column dividers)
  hud: Container;
  spinButton: Container;
  betSelector: Container;
  menuButton: Container;
  autoplayPanel: Container;
  paytable: Container;
  buyFeatureButton?: Container;
  chevronBtn?: Container;
  turboButton?: Container;
  logo?: Container;
  title?: Container;
  statusMessage?: Container;
  holdForSettings?: Container;
  jackpotPanel?: Container;
  pileDiamond?: Container;
}

/** Minimum touch target size in design pixels for portrait mode */
const MIN_TOUCH_TARGET = 80;

/** Orchestrates responsive layout switching and element repositioning */
export class ResponsiveLayout {
  private _app: Application;
  private _orientation: Orientation = 'landscape';
  private _elements: LayoutElements | null = null;

  // Cached reel dimensions
  private _reelTotalW: number = 0;
  private _reelTotalH: number = 0;

  // External resize callbacks
  private _onResizeCallbacks: Array<(designW: number, designH: number) => void> = [];

  constructor(app: Application) {
    this._app = app;
    this._setupResizeHandler();
  }

  /** Register a callback to receive updated design dimensions on every resize */
  onResize(cb: (designW: number, designH: number) => void): void {
    this._onResizeCallbacks.push(cb);
  }

  get orientation(): Orientation {
    return this._orientation;
  }

  /** Bind all layout elements for repositioning on resize */
  setElements(elements: LayoutElements, reelTotalW: number, reelTotalH: number): void {
    this._elements = elements;
    this._reelTotalW = reelTotalW;
    this._reelTotalH = reelTotalH;
    this.resize();
  }

  /** Recompute scale and reposition all elements */
  resize(): void {
    const { innerWidth: w, innerHeight: h } = window;
    const isLandscape = w >= h;
    this._orientation = isLandscape ? 'landscape' : 'portrait';

    const designWidth = isLandscape
      ? GameConfig.DESIGN_WIDTH_LANDSCAPE
      : GameConfig.DESIGN_WIDTH_PORTRAIT;
    const designHeight = isLandscape
      ? GameConfig.DESIGN_HEIGHT_LANDSCAPE
      : GameConfig.DESIGN_HEIGHT_PORTRAIT;

    const scale = Math.min(w / designWidth, h / designHeight);

    this._app.stage.scale.set(scale);
    this._app.stage.position.set(
      (w - designWidth * scale) / 2,
      (h - designHeight * scale) / 2,
    );

    this._app.renderer.resize(w, h);

    if (this._elements) {
      this._repositionElements(isLandscape, designWidth, designHeight);
    }

    logger.debug(`Resize: ${w}x${h}, ${this._orientation}, scale=${scale.toFixed(3)}`);

    // Notify external listeners with updated design dimensions
    for (const cb of this._onResizeCallbacks) {
      cb(designWidth, designHeight);
    }
  }

  private _repositionElements(
    isLandscape: boolean,
    designW: number,
    designH: number,
  ): void {
    const el = this._elements!;
    const rw = this._reelTotalW;
    const rh = this._reelTotalH;

    if (isLandscape) {
      this._layoutLandscape(el, designW, designH, rw, rh);
    } else {
      this._layoutPortrait(el, designW, designH, rw, rh);
    }
  }

  /** === LANDSCAPE / DESKTOP === */
  private _layoutLandscape(
    el: LayoutElements,
    designW: number,
    designH: number,
    rw: number,
    rh: number,
  ): void {
    // Frame dimensions from source asset
    const frameW = 1921;
    const frameH = 1334;
    const chromeW = 1540;
    const COL_CENTERS = [384, 672, 961, 1249, 1537];
    const REEL_AREA_TOP = 320;
    const REEL_AREA_BOTTOM = 1050;
    const COL_WIDTH = 272;

    // Scale chrome to fill full viewport width, like portrait mode does
    const frameScale = designW / chromeW;
    const scaledFrameW = frameW * frameScale;
    const scaledFrameH = frameH * frameScale;

    // Center the reel area vertically in the viewport
    // Reel area center in frame space: (320 + 1050) / 2 = 685
    const reelCenterInFrame = (REEL_AREA_TOP + REEL_AREA_BOTTOM) / 2;
    // We want reelCenterInFrame * frameScale to be at designH * 0.45 (slightly above center to leave room for HUD)
    const targetReelCenterY = designH * 0.45;
    const frameTopY = targetReelCenterY - reelCenterInFrame * frameScale;

    const frameCenterX = designW / 2;

    if (el.reelFrame) {
      el.reelFrame.visible = true;
      el.reelFrame.x = frameCenterX;
      el.reelFrame.y = frameTopY + scaledFrameH / 2;
      el.reelFrame.scale.set(frameScale);
    }

    if (el.reelFrameFg) {
      el.reelFrameFg.visible = true;
      el.reelFrameFg.x = frameCenterX;
      el.reelFrameFg.y = frameTopY + scaledFrameH / 2;
      el.reelFrameFg.scale.set(frameScale);
    }

    // Frame left edge in design space
    const frameLeftX = frameCenterX - scaledFrameW / 2;

    // Column centers in design space
    const colCentersDesign = COL_CENTERS.map(cx => frameLeftX + cx * frameScale);
    const colWidthDesign = COL_WIDTH * frameScale;
    const reelScale = colWidthDesign / GameConfig.SYMBOL_WIDTH;
    const scaledRW = rw * reelScale;
    const scaledRH = rh * reelScale;

    // Reel area vertical bounds
    const reelAreaTopDesign = frameTopY + REEL_AREA_TOP * frameScale;
    const reelAreaBottomDesign = frameTopY + REEL_AREA_BOTTOM * frameScale;
    const reelAreaH = reelAreaBottomDesign - reelAreaTopDesign;

    const reelX = colCentersDesign[0];
    const reelY = reelAreaTopDesign + (reelAreaH - scaledRH) / 2;

    el.reelSet.x = reelX;
    el.reelSet.y = reelY;
    el.reelSet.scale.set(reelScale);

    // Hide dark panel bg — frame provides structure
    const bg = el.reelBg as unknown as Graphics;
    bg.clear();

    // Show separators
    const reelSetInstance = el.reelSet as unknown as ReelSet;
    if (typeof reelSetInstance.setSeparatorsVisible === 'function') {
      reelSetInstance.setSeparatorsVisible(true);
    }

    // Spin button overlays on right edge of chrome
    const spinBtnX = designW - 80;
    const spinBtnY = reelAreaTopDesign + reelAreaH / 2;
    el.spinButton.x = spinBtnX;
    el.spinButton.y = spinBtnY;
    el.spinButton.scale.set(0.85);

    // "HOLD FOR SETTINGS" — visible in landscape, overlaying spin button
    if (el.holdForSettings) {
      el.holdForSettings.x = spinBtnX;
      el.holdForSettings.y = spinBtnY;
      el.holdForSettings.visible = true;
      const arcBaseRadius = 75;
      const spinRadius = (208 * 0.85) / 2;
      const desiredArcRadius = spinRadius + 8;
      el.holdForSettings.scale.set(desiredArcRadius / arcBaseRadius);
    }

    // Jackpot panel — top-center-right
    if (el.jackpotPanel) {
      el.jackpotPanel.x = designW * 0.55;
      el.jackpotPanel.y = 15;
      el.jackpotPanel.scale.set(0.65);
    }

    // Menu button — top-right corner
    el.menuButton.x = designW - 45;
    el.menuButton.y = 25;

    // HUD bar — full width at very bottom
    const hudNaturalW = 1200;
    const hudScale = designW / hudNaturalW;
    el.hud.x = 0;
    el.hud.y = designH - (100 * hudScale);
    el.hud.scale.set(hudScale);

    // Status message — bottom-right, small
    if (el.statusMessage) {
      el.statusMessage.x = designW - 150;
      el.statusMessage.y = designH - 15;
      el.statusMessage.visible = true;
    }

    // Chevron / bet button — left side of reels, centered with actual reel area
    if (el.chevronBtn) {
      el.chevronBtn.x = 60;
      el.chevronBtn.y = reelAreaTopDesign + reelAreaH / 2;
      el.chevronBtn.scale.set(0.7);
    }

    // Hide Buy Feature, Turbo, Auto in landscape
    if (el.buyFeatureButton) el.buyFeatureButton.visible = false;
    if (el.turboButton) el.turboButton.visible = false;
    el.autoplayPanel.visible = false;

    // Bet selector — near chevron on left
    el.betSelector.x = 60;
    el.betSelector.y = designH * 0.35;
    el.betSelector.scale.set(0.6);

    // Logo — top-left, small
    if (el.logo) {
      el.logo.visible = true;
      el.logo.x = designW * 0.15;
      el.logo.y = 60;
      el.logo.scale.set(frameScale * 0.35);
    }
    if (el.title) {
      el.title.visible = true;
      el.title.x = designW * 0.15;
      el.title.y = 60;
    }

    // Diamond pile — top-center, overlapping frame top
    if (el.pileDiamond) {
      el.pileDiamond.x = designW * 0.42;
      el.pileDiamond.y = 20;
      el.pileDiamond.scale.set(frameScale * 0.4);
      el.pileDiamond.visible = true;
    }

    logger.debug(`Landscape: frameScale=${frameScale.toFixed(3)}, scaledFrameW=${scaledFrameW.toFixed(0)}, scaledFrameH=${scaledFrameH.toFixed(0)}`);
  }

  /** === PORTRAIT (mobile-optimized) === */
  private _layoutPortrait(
    el: LayoutElements,
    designW: number,
    designH: number,
    rw: number,
    rh: number,
  ): void {
    // Portrait: Show the reel frame and fit reels INSIDE it
    // The frame image (reelframe_base.png, 1921x1334) has 5 columns for 5 reels.
    // Scale the frame to fill viewport width, then position reels inside.

    // Frame fills full width — scale based on the CHROME part, not the full image
    // The image (1921x1334) has blue bg padding. Chrome starts at ~x=190, ends at ~x=1730.
    // Chrome width = ~1540px out of 1921px total.
    const frameW = 1921;
    const frameH = 1334;
    const chromeW = 1540; // actual chrome border width within image
    const frameScale = designW / chromeW; // scale chrome to fill viewport edge-to-edge
    const scaledFrameW = frameW * frameScale;
    const scaledFrameH = frameH * frameScale;

    // Column centers in the frame image (measured from pixel data):
    // Separators at x: 528, 816, 1105, 1393. Columns between them:
    // Col 0: center=384, Col 1: center=672, Col 2: center=961, Col 3: center=1249, Col 4: center=1537
    // Column width ~272px. Top of reel area ~320, bottom ~1050.
    const COL_CENTERS = [384, 672, 961, 1249, 1537]; // in image pixels
    const REEL_AREA_TOP = 320;  // image pixels
    const REEL_AREA_BOTTOM = 1050;
    const COL_WIDTH = 272; // approximate column width in image pixels

    // Position frame at top of content area
    const frameY = designH * 0.15;

    if (el.reelFrame) {
      el.reelFrame.visible = true;
      el.reelFrame.x = designW / 2;
      el.reelFrame.y = frameY + scaledFrameH / 2;
      el.reelFrame.scale.set(frameScale);
    }

    // Reel frame foreground — same position and scale as reelFrame
    if (el.reelFrameFg) {
      el.reelFrameFg.visible = true;
      el.reelFrameFg.x = designW / 2;
      el.reelFrameFg.y = frameY + scaledFrameH / 2;
      el.reelFrameFg.scale.set(frameScale);
    }

    // Frame image left edge in design space (frame is center-anchored)
    const frameLeftX = designW / 2 - scaledFrameW / 2;
    const frameTopY = frameY;

    // Convert column centers from image space to design space
    const colCentersDesign = COL_CENTERS.map(cx => frameLeftX + cx * frameScale);

    // Scale reel symbols to fit inside a column
    // Each symbol should fit within the column width
    const colWidthDesign = COL_WIDTH * frameScale;
    const reelScale = colWidthDesign / GameConfig.SYMBOL_WIDTH;
    const scaledRW = rw * reelScale;
    const scaledRH = rh * reelScale;

    // Reel area vertical bounds in design space
    const reelAreaTopDesign = frameTopY + REEL_AREA_TOP * frameScale;
    const reelAreaBottomDesign = frameTopY + REEL_AREA_BOTTOM * frameScale;
    const reelAreaH = reelAreaBottomDesign - reelAreaTopDesign;

    // Position ReelSet so each reel[i] center aligns with colCentersDesign[i]
    // In ReelSet, reel i is at local x = i * (SYMBOL_WIDTH + REEL_GAP)
    // Reel i center in design space = reelX + i * (SYMBOL_WIDTH + REEL_GAP) * reelScale
    // We want this to equal colCentersDesign[i]
    // Using reel 0: reelX + 0 = colCentersDesign[0]
    const reelX = colCentersDesign[0];
    const reelY = reelAreaTopDesign + (reelAreaH - scaledRH) / 2;

    el.reelSet.x = reelX;
    el.reelSet.y = reelY;
    el.reelSet.scale.set(reelScale);

    // Show reel separators (frame provides the visual boundary)
    const reelSetInstance = el.reelSet as unknown as ReelSet;
    if (typeof reelSetInstance.setSeparatorsVisible === 'function') {
      reelSetInstance.setSeparatorsVisible(true);
    }

    // Hide the dark panel backgrounds — the frame replaces them
    const bg = el.reelBg as unknown as Graphics;
    bg.clear();

    // HUD: gold bar edge-to-edge, directly below the chrome frame bottom
    // Chrome bottom is at approximately 82% of the frame image height
    const chromeBottomRatio = 0.82;
    const frameBottom = frameY + frameH * frameScale * chromeBottomRatio;

    // Scale HUD to fill full viewport width
    // HUD panelWidth = reelTotalW + 40 (set in main.ts constructor)
    // We need to scale it to fill designW
    const hudPanelW = this._reelTotalW + 40; // match what was passed to HUD constructor
    const hudScale = designW / hudPanelW;

    // The HUD has a bottom_shadow.png sprite that extends UPWARD from y=0 in HUD local space.
    // Shadow texture is 129px tall, scaled to hudPanelW / 1449 in HUD local coords,
    // then further scaled by hudScale. If the HUD is positioned too close to the reels,
    // the shadow gradient overlaps and dims the bottom row of symbols.
    // Ensure HUD Y is far enough below the reel area that the shadow doesn't overlap.
    const shadowTexH = 129;
    const shadowLocalScale = hudPanelW / 1449;
    const shadowDesignH = shadowTexH * shadowLocalScale * hudScale;
    const hudY = Math.max(frameBottom, reelAreaBottomDesign + shadowDesignH + 5);

    el.hud.x = 0;
    el.hud.y = hudY;
    el.hud.scale.set(hudScale);
    // HUD barHeight is 100 in local coords
    const hudHeight = 100 * hudScale;

    // Status message ("PRESS SPIN TO START GAME") — just below HUD bar, above controls
    // Position it at hudY + hudHeight + small gap so it sits between HUD and spin button
    const statusY = hudY + hudHeight + 20;

    if (el.statusMessage) {
      el.statusMessage.x = designW - 30;
      el.statusMessage.y = hudY + hudHeight + 10;
      el.statusMessage.visible = true;
    }

    // === CONTROL AREA: Buy Feature | Spin | Turbo | Auto ===
    // All buttons on the same horizontal Y line with absolute X positions.
    // Layout (left to right): [BUY FEATURE] ... [SPIN] ... [TURBO] [AUTO]
    const spinY = designH * 0.78;
    const spinButtonScale = (designW * 0.22) / 208;
    const spinRadius = (208 * spinButtonScale) / 2; // used for holdForSettings offset

    // Spin — dead center
    el.spinButton.x = designW / 2;
    el.spinButton.y = spinY;
    el.spinButton.scale.set(spinButtonScale);

    // "HOLD FOR SETTINGS" curved text — centered on spin button
    if (el.holdForSettings) {
      el.holdForSettings.x = designW / 2;
      el.holdForSettings.y = spinY;
      // Scale the arc container so its 75px base radius matches the actual spin radius + small offset
      const arcBaseRadius = 75;
      const desiredArcRadius = spinRadius + 8;
      const arcScale = desiredArcRadius / arcBaseRadius;
      el.holdForSettings.scale.set(arcScale);
      el.holdForSettings.visible = true;
    }

    // Bet controls — overlay panel (hidden by default, toggled via long-press)
    el.betSelector.x = designW / 2;
    el.betSelector.y = spinY - spinRadius * 2 - 30;
    el.betSelector.scale.set(0.8);

    // Buy Feature button — LEFT of spin at ~20% of designW
    if (el.buyFeatureButton) {
      el.buyFeatureButton.visible = true;
      el.buyFeatureButton.x = designW * 0.20;
      el.buyFeatureButton.y = spinY;
      el.buyFeatureButton.scale.set(0.5);
    }

    // Chevron ">" button — far left of control row
    if (el.chevronBtn) {
      el.chevronBtn.x = designW * 0.07;
      el.chevronBtn.y = spinY;
      el.chevronBtn.scale.set(0.7);
    }

    // Turbo button — RIGHT of spin at ~72% of designW
    if (el.turboButton) {
      el.turboButton.x = designW * 0.72;
      el.turboButton.y = spinY;
      el.turboButton.scale.set(0.8);
    }

    // Auto button — RIGHT of turbo at ~85% of designW
    el.autoplayPanel.x = designW * 0.85;
    el.autoplayPanel.y = spinY;
    el.autoplayPanel.scale.set(0.8);

    // Menu button — top right (70x70 button, so offset by 35+margin)
    el.menuButton.x = designW - 80;
    el.menuButton.y = 60;

    // Jackpot panel — top right, below menu button
    // Right edge of widest row (380px) aligns with designW - 20.
    // Y: just below menu button (Y=60), starting at ~frameY * 0.42 (~120px).
    if (el.jackpotPanel) {
      el.jackpotPanel.x = designW - 400;
      el.jackpotPanel.y = 120;
      el.jackpotPanel.scale.set(1.0);
    }

    // Diamond pile — large, top-left, overlapping into reel frame top
    // Reference: pile spans ~65% viewport width, centered slightly left,
    // crystals extend from top down into the first row of reels
    if (el.pileDiamond) {
      const pileTargetW = designW * 0.70;
      const pileNativeW = 1747;
      const pileScale = pileTargetW / pileNativeW;
      el.pileDiamond.scale.set(pileScale);
      // Position: slightly left of center, overlapping into reel frame top
      el.pileDiamond.x = designW * 0.38;
      el.pileDiamond.y = frameY + scaledFrameH * 0.12;
      el.pileDiamond.visible = true;
    }

    // Logo — top-left, overlapping slightly with pile
    if (el.logo) {
      const logoTargetWidth = designW * 0.45;
      const logoNaturalWidth = el.logo.width / (el.logo.scale.x || 1);
      const logoScale = logoTargetWidth / logoNaturalWidth;
      el.logo.scale.set(logoScale);
      // Anchor is (0.5, 1) — position in top-left area
      el.logo.x = designW * 0.28;
      // Logo bottom edge near top of reel frame
      el.logo.y = frameY - 15;
      el.logo.visible = true;
    }
    if (el.title) {
      el.title.x = designW * 0.28;
      el.title.y = frameY - 15;
      el.title.visible = true;
    }

    logger.debug(`Portrait: reelY=${reelY.toFixed(0)}, scaledRH=${scaledRH.toFixed(0)}, hudY=${el.hud.y.toFixed(0)}, spinY=${el.spinButton.y.toFixed(0)}, designH=${designH}`);
  }

  /** Position and resize the reel background to match the scaled reel grid exactly (landscape) */
  private _alignReelBg(
    el: LayoutElements,
    reelX: number,
    reelY: number,
    scaledRW: number,
    scaledRH: number,
  ): void {
    const padX = 16;
    const padY = 10;
    const bg = el.reelBg as unknown as Graphics;
    bg.clear();
    bg.roundRect(0, 0, scaledRW + padX * 2, scaledRH + padY * 2, 12);
    bg.fill({ color: 0x000000, alpha: 0.5 });
    bg.x = reelX - padX;
    bg.y = reelY - padY;

    // Restore separators in landscape
    const reelSetInstance = el.reelSet as unknown as ReelSet;
    if (typeof reelSetInstance.setSeparatorsVisible === 'function') {
      reelSetInstance.setSeparatorsVisible(true);
    }
  }

  /** FIX 3: Draw individual dark blue panels behind each reel column (portrait only) */
  private _alignReelBgPortrait(
    el: LayoutElements,
    reelX: number,
    reelY: number,
    scaledRW: number,
    scaledRH: number,
    reelScale: number,
    _rawRW: number,
  ): void {
    const bg = el.reelBg as unknown as Graphics;
    bg.clear();

    const reelCount = GameConfig.VISUAL_REEL_COUNT;
    const symbolW = GameConfig.SYMBOL_WIDTH * reelScale;
    const reelGap = GameConfig.REEL_GAP * reelScale;
    const padX = 4;
    const padY = 2;

    for (let i = 0; i < reelCount; i++) {
      const rx = i * (symbolW + reelGap) - padX;
      const ry = -padY;
      const rw = symbolW + padX * 2;
      const rh = scaledRH + padY * 2;

      // Dark blue panel background
      bg.roundRect(rx, ry, rw, rh, 8);
      bg.fill({ color: 0x0a1a3a, alpha: 0.9 });

      // Subtle lighter border
      bg.roundRect(rx, ry, rw, rh, 8);
      bg.stroke({ color: 0x1a3a6a, width: 1, alpha: 0.7 });
    }

    bg.x = reelX;
    bg.y = reelY;
  }

  private _setupResizeHandler(): void {
    let resizeTimeout: ReturnType<typeof setTimeout>;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => this.resize(), 100);
    });
  }
}
